import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { buildTemuPath } from '@/temu-platform/constants/temuRoutes';
import { useTemuPageShopScope } from '@/temu-platform/hooks/useTemuPageShopScope';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { pickField } from '@/temu-platform/utils/temuResponse';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';

/**
 * 半托售后详情：文档内可处理动作（同意取消 / 全额退款 / 退货标签相关）。
 * 不支持：拒绝退货、部分退款、申诉。
 */
export default function SemiReturnOrderDetailPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const { returnOrderNo } = useParams();
  const [searchParams] = useSearchParams();
  const { concreteScopeId } = useTemuPageShopScope();
  const shopId = concreteScopeId;
  const gatewayRegion = String(searchParams.get('gatewayRegion') || '').toUpperCase() || undefined;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState('');
  const [error, setError] = useState('');
  const [aux, setAux] = useState(null);

  const parentAfterSalesSn = String(returnOrderNo || '').trim();

  const load = useCallback(async () => {
    if (!shopId || !parentAfterSalesSn) return;
    setLoading(true);
    setError('');
    try {
      const res = await temuPlatformAPI.getSemiAftersalesDetail(shopId, parentAfterSalesSn, { gatewayRegion });
      setDetail(res?.item || res?.detail || res);
    } catch (e) {
      setDetail(null);
      setError(e?.message || t('returnDetail.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [shopId, parentAfterSalesSn, gatewayRegion, t]);

  useEffect(() => { load(); }, [load]);

  const status = Number(pickField(detail, 'afterSalesStatus', 'after_sales_status', 'parentAfterSalesStatus'));
  const canAgreeCancel = useMemo(() => status === 1 || status === 2 || pickField(detail, 'cancelPending') === true, [detail, status]);
  const canRefund = useMemo(() => !Number.isNaN(status) && status > 0, [status]);

  const runAction = async (key, fn, okMsg) => {
    if (!shopId || !parentAfterSalesSn) return;
    const ok = await showTemuConfirm({ title: t('returnDetail.confirmAction'), message: okMsg || t('returnDetail.confirmRun', { action: key }) });
    if (!ok) return;
    setActing(key);
    try {
      const res = await fn();
      setAux(res);
      showToast(t('returnDetail.actionOk'), 'success');
      await load();
    } catch (e) {
      showToast(e?.message || t('returnDetail.actionFailed'), 'error');
    } finally {
      setActing('');
    }
  };

  return (
    <div className="tpo-detail">
      <div className="tpo-detail__header">
        <button type="button" className="tpo-btn" onClick={() => navigate(buildTemuPath('orders/returns'))}>
          {t('orderList.backToList')}
        </button>
        <h2 className="tpo-detail__title">{t('returnDetail.semiTitle', { sn: parentAfterSalesSn })}</h2>
        {gatewayRegion ? <span className="tpo-badge">{gatewayRegion}</span> : null}
      </div>

      {loading ? <div className="tpo-empty">{t('returnList.loading')}</div> : null}
      {error ? <div className="tpo-empty tpo-empty--error">{error}</div> : null}

      {detail ? (
        <>
          <div className="tpo-detail__meta">
            <div>{t('returnDetail.relatedPrefix')}{pickField(detail, 'parentOrderSn', 'parent_order_sn') || '—'}</div>
            <div>{t('returnDetail.statusPrefix')}{pickField(detail, 'afterSalesStatus', 'statusLabel') ?? '—'}</div>
            <div>{t('returnDetail.amountPrefix')}{pickField(detail, 'amountSummary', 'amount_summary') || '—'}</div>
          </div>

          <div className="tpo-detail__actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '12px 0' }}>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary"
              disabled={!!acting || !canAgreeCancel}
              onClick={() => runAction('agreeCancel', () => temuPlatformAPI.agreeSemiAftersalesCancel(shopId, {
                parentAfterSalesSn,
                gatewayRegion,
              }), t('returnDetail.agreeCancelMsg'))}
            >
              {acting === 'agreeCancel' ? t('returnList.processing') : t('returnDetail.agreeCancel')}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary"
              disabled={!!acting || !canRefund}
              onClick={() => runAction('issueRefund', () => temuPlatformAPI.issueSemiAftersalesRefund(shopId, {
                parentAfterSalesSn,
                gatewayRegion,
                openApiRefundType: 1,
              }), t('returnDetail.fullRefundMsg'))}
            >
              {acting === 'issueRefund' ? t('returnList.processing') : t('returnDetail.fullRefund')}
            </button>
            <button
              type="button"
              className="tpo-btn"
              disabled={!!acting}
              onClick={() => runAction('prepareLabel', () => temuPlatformAPI.prepareSemiReturnLabel(shopId, {
                parentAfterSalesSn,
                gatewayRegion,
              }), t('returnDetail.prepareLabelMsg'))}
            >
              {acting === 'prepareLabel' ? t('returnList.processing') : t('returnDetail.prepareLabel')}
            </button>
            <button
              type="button"
              className="tpo-btn"
              disabled={!!acting}
              onClick={() => runAction('carrier', () => temuPlatformAPI.getSemiAftersalesCarrier(shopId, {
                parentAfterSalesSn,
                gatewayRegion,
              }))}
            >
              {t('returnDetail.lookupCarrier')}
            </button>
            <button
              type="button"
              className="tpo-btn"
              disabled={!!acting}
              onClick={() => runAction('address', () => temuPlatformAPI.getSemiReturnAddress(shopId, {
                parentAfterSalesSn,
                gatewayRegion,
              }))}
            >
              {t('returnDetail.lookupAddress')}
            </button>
            <button
              type="button"
              className="tpo-btn"
              disabled={!!acting}
              onClick={() => runAction('signature', () => temuPlatformAPI.getSemiAftersalesSignature(shopId, {
                parentAfterSalesSn,
                gatewayRegion,
              }))}
            >
              {t('returnDetail.lookupSignature')}
            </button>
          </div>

          <p className="tpo-hint">{t('returnDetail.unsupported')}</p>

          {aux ? (
            <pre className="tpo-code-block" style={{ maxHeight: 280, overflow: 'auto', fontSize: 12 }}>
              {JSON.stringify(aux, null, 2)}
            </pre>
          ) : null}

          <pre className="tpo-code-block" style={{ maxHeight: 420, overflow: 'auto', fontSize: 12 }}>
            {JSON.stringify(detail, null, 2)}
          </pre>
        </>
      ) : null}
    </div>
  );
}
